import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import { messagingApi } from "messaging/_api/messaging-api";
import { ForumJSON } from "messaging/forums/_schemas/forum-schema";

type ForumUnreadCountsState = {
  /** The reader's unread count for each forum, by forum id, as the server last said it. */
  entries: Record<string, number>;
};

const initialState: ForumUnreadCountsState = {
  entries: {},
};

function recordForums(state: ForumUnreadCountsState, forums: readonly ForumJSON[]): void {
  for (const forum of forums) {
    if (forum.id && typeof forum.numUnreadMessages === "number") {
      state.entries[forum.id] = forum.numUnreadMessages;
    }
  }
}

/**
 * Every conversation tab's unread count (#5828): the server's count of the reader's unread messages in
 * the forum, all of them, loaded on this screen or not.
 *
 * Each count is the newest one the server sent. It sends one with every list of forums, with the answer
 * to every read-receipt write, with the answer to Mark all read, and with every realtime message event,
 * where it is the recipient's own count. So a tab the reader is not on still moves when a message lands
 * in it, and a tab the reader has read stays at its new count after they leave it.
 *
 * Not the forum's own `numUnreadMessages`: a forum object is a snapshot from when its list loaded, and a
 * tab that read it showed the count from before the reader read anything.
 */
const forumUnreadCountsSlice = createSlice({
  name: "forumUnreadCounts",
  initialState,
  reducers: {
    /** A realtime message event carried the reader's count for its forum. */
    forumUnreadCountReported: (state, action: PayloadAction<{ forumId: string; unreadCount: number }>) => {
      state.entries[action.payload.forumId] = action.payload.unreadCount;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(messagingApi.endpoints.getForums.matchFulfilled, (state, action) => {
      recordForums(state, action.payload);
    });
    builder.addMatcher(messagingApi.endpoints.getForumsByIds.matchFulfilled, (state, action) => {
      recordForums(state, action.payload.found);
    });
    // Each answer counts after its own write. A batch can touch several forums; the last answer for a
    // forum is the newest count for it.
    builder.addMatcher(messagingApi.endpoints.createBulkMessages.matchFulfilled, (state, action) => {
      for (const message of action.payload) {
        const forumId = message.forum.id?.value;
        if (forumId && typeof message.forum.numUnreadMessages === "number") {
          state.entries[forumId] = message.forum.numUnreadMessages;
        }
      }
    });
    builder.addMatcher(messagingApi.endpoints.markForumRead.matchFulfilled, (state, action) => {
      state.entries[action.payload.forumId] = action.payload.numUnreadMessages;
    });
  },
});

export const { forumUnreadCountReported } = forumUnreadCountsSlice.actions;

/** The reader's unread count for every forum the server has sent one for, by forum id. */
export const getForumUnreadCounts = (state: RootState) => state.forumUnreadCounts.entries;

/** The reader's unread count for the forum, or undefined while the server has sent none. */
export const getForumUnreadCount = (forumId: string | undefined) => (state: RootState) =>
  forumId ? state.forumUnreadCounts.entries[forumId] : undefined;

export default forumUnreadCountsSlice;
